import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, ReplaySubject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { IUser } from '../../../core/models/auth.models';
import { IProductMain } from '../../../core/models/products.models';
import { AppService } from '../../../store/app.service';
import { ShopApiService } from '../shop.apiservice';
import { UtilityService } from '../../../core/helpers/utility.service';
import { ROLES_TYPE } from '../../../core/helpers/constants';
import { SubscriptionApiService } from '../../subscriptions/subscription.apiservice';
import { ClientsApiService } from '../../clients/clients.apiservice';
import { TranslateService } from '@ngx-translate/core';

type TierName = 'TIER_BASIC' | 'TIER_STANDARD' | 'TIER_PREMIUM';

type FeatureKey =
  | 'reachPlatform'
  | 'weeklyReports'
  | 'realTimeDashboard'
  | 'updateEmails'
  | 'dedicatedDms'
  | 'standardAds'
  | 'landingPages'
  | 'leadForms'
  | 'campaignCustomization'
  | 'creativeEdits'
  | 'localCreative'
  | 'strategyCalls'
  | 'discounts';

type FeatureDefinition = {
  defaultLabel: string;
  labelOverrides?: Partial<Record<TierName, string>>;
  descriptionOverrides?: Partial<Record<TierName, string>>;
  defaultDescription?: string;
};

const FEATURE_DEFINITIONS: Record<FeatureKey, FeatureDefinition> = {
  reachPlatform: {
    defaultLabel: 'REACH_PLATFORM'
  },
  weeklyReports: {
    defaultLabel: 'WEEKLY_REPORTS'
  },
  realTimeDashboard: {
    defaultLabel: 'REAL_TIME_DASHBOARD'
  },
  updateEmails: {
    defaultLabel: 'REGULAR_CAMPAIGN_UPDATE_EMAILS'
  },
  dedicatedDms: {
    defaultLabel: 'DEDICATED_DMS'
  },
  standardAds: {
    defaultLabel: 'STANDARD_ADS'
  },
  landingPages: {
    defaultLabel: 'DEFAULT_LANDING_PAGES',
    labelOverrides: {
      TIER_PREMIUM: 'CUSTOM_LANDING_PAGES'
    },
    descriptionOverrides: {
      TIER_PREMIUM: 'CUSTOM_LANDING_PAGES_DESC'
    }
  },
  leadForms: {
    defaultLabel: 'DEFAULT_LEAD_FORMS',
    labelOverrides: {
      TIER_STANDARD: 'CUSTOM_LEAD_FORMS',
      TIER_PREMIUM: 'CUSTOM_LEAD_FORMS'
    }
  },
  campaignCustomization: {
    defaultLabel: 'CAMPAIGN_CUSTOMIZATION',
    labelOverrides: {
      TIER_STANDARD: 'LIMITED_CAMPAIGN_CUSTOMIZATION',
      TIER_PREMIUM: 'UNLIMITED_CAMPAIGN_CUSTOMIZATION'
    },
    descriptionOverrides: {
      TIER_STANDARD: 'LIMITED_CAMPAIGN_CUSTOMIZATION_DESC',
      TIER_PREMIUM: 'UNLIMITED_CAMPAIGN_CUSTOMIZATION_DESC'
    }
  },
  creativeEdits: {
    defaultLabel: 'CUSTOM_CREATIVE_EDITS',
    descriptionOverrides: {
      TIER_PREMIUM: 'CUSTOM_CREATIVE_EDITS_DESC'
    }
  },
  localCreative: {
    defaultLabel: 'LOCAL_CREATIVE'
  },
  strategyCalls: {
    defaultLabel: 'CAMPAIGN_CALLS',
    labelOverrides: {
      TIER_STANDARD: 'ONE_CAMPAIGN_CALL_PER_MONTH',
      TIER_PREMIUM: 'TWO_CAMPAIGN_CALLS_PER_MONTH'
    }
  },
  discounts: {
    defaultLabel: 'DISCOUNTS_AVAILABLE'
  }
};

@Component({
  selector: 'app-shop-front',
  templateUrl: './shop-front.component.html',
  styleUrls: ['./shop-front.component.scss']
})
export class ShopFrontComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  allProducts: Array<IProductMain> = new Array<IProductMain>();
  companyProducts: Array<IProductMain> = new Array<IProductMain>();
  tieredProducts: Array<any> = [];

  subscriptionData: Array<any>  = new Array<any>();
  existingSubscriptionTier: number | null = null;

  public userRoles: Array<number> = [];
  activeUser: IUser = {} as IUser;
  isExternal: boolean = false;

  featureKeys: FeatureKey[] = [
    'reachPlatform',
    'weeklyReports',
    'realTimeDashboard',
    'updateEmails',
    'dedicatedDms',
    'standardAds',
    'landingPages',
    'leadForms',
    'campaignCustomization',
    'creativeEdits',
    'localCreative',
    'strategyCalls',
    // 'discounts'
  ];

  private readonly tierDisplayCandidates: Record<TierName, string[]> = {
    TIER_BASIC: ['TIER_BASIC', 'BASIC_TIER', 'Basic Tier', 'Basic', 'Básico'],
    TIER_STANDARD: ['TIER_STANDARD', 'STANDARD_TIER', 'Standard Tier', 'Standard', 'Estándar'],
    TIER_PREMIUM: ['TIER_PREMIUM', 'PREMIUM_TIER', 'Premium Tier', 'Premium', 'Premium']
  };

  private readonly tierProductTypeCandidates: Record<'single' | 'multi', string[]> = {
    single: ['SINGLE_PLATFORM', 'Single Platform', 'Single', 'Una sola plataforma', 'Plataforma única'],
    multi: ['MULTI_PLATFORM', 'Multi Platform', 'Multi', 'Multiplataforma']
  };

  getFeatureLabel(tier: { name: TierName }, featureKey: FeatureKey): string {
    const def = FEATURE_DEFINITIONS[featureKey];
    return def.labelOverrides?.[tier.name] ?? def.defaultLabel;
  }

  getFeatureDescription(tier: { name: TierName }, featureKey: FeatureKey): string | null {
    const def = FEATURE_DEFINITIONS[featureKey];
    return def.descriptionOverrides?.[tier.name] ?? def.defaultDescription ?? null;
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  private matchesAnyCandidate(value: unknown, candidates: string[]): boolean {
    const normalizedValue = this.normalizeText(value);
    if (!normalizedValue) return false;

    return candidates
      .map(candidate => this.normalizeText(candidate))
      .filter(Boolean)
      .some(candidate => normalizedValue.includes(candidate));
  }

  private getTierDisplayCandidates(tierName: TierName): string[] {
    const translatedTier = this.translate.instant(tierName);
    return [
      ...(this.tierDisplayCandidates[tierName] ?? []),
      translatedTier
    ].filter(Boolean);
  }

  private isTierProduct(productName: unknown, tierName: TierName): boolean {
    return this.matchesAnyCandidate(productName, this.getTierDisplayCandidates(tierName));
  }

  private isSinglePlatformProduct(productName: unknown): boolean {
    return this.matchesAnyCandidate(productName, this.tierProductTypeCandidates.single);
  }

  private isMultiPlatformProduct(productName: unknown): boolean {
    return this.matchesAnyCandidate(productName, this.tierProductTypeCandidates.multi);
  }

  isTiered: boolean = false;
  hasSetup: boolean = false;

  pricingTiers: Array<{
    name: TierName;
    id: number;
    description: string;
    singlePrice: number | null;
    multiPrice: number | null;
    singleSignupFee: number | null;
    multiSignupFee: number | null;
    features: Record<FeatureKey, boolean>;
  }> = [
    {
      name: 'TIER_BASIC',
      id: 1,
      description: 'TIER_BASIC_DESC',
      singlePrice: null,
      multiPrice: null,
      singleSignupFee: null,
      multiSignupFee: null,
      features: {
        reachPlatform: true,
        weeklyReports: true,
        realTimeDashboard: true,
        dedicatedDms: false,
        standardAds: true,
        landingPages: true,
        leadForms: true,
        campaignCustomization: false,
        creativeEdits: false,
        localCreative: false,
        strategyCalls: false,
        updateEmails: true,
        discounts: false
      }
    },
    {
      name: 'TIER_STANDARD',
      id: 2,
      description: 'TIER_STANDARD_DESC',
      singlePrice: null,
      multiPrice: null,
      singleSignupFee: null,
      multiSignupFee: null,
      features: {
        reachPlatform: true,
        weeklyReports: true,
        realTimeDashboard: true,
        dedicatedDms: true,
        standardAds: true,
        landingPages: true,
        leadForms: true,
        campaignCustomization: true,
        creativeEdits: false,
        localCreative: false,
        strategyCalls: true,
        updateEmails: true,
        discounts: false
      }
    },
    {
      name: 'TIER_PREMIUM',
      id: 3,
      description: 'TIER_PREMIUM_DESC',
      singlePrice: null,
      multiPrice: null,
      singleSignupFee: null,
      multiSignupFee: null,
      features: {
        reachPlatform: true,
        weeklyReports: true,
        realTimeDashboard: true,
        dedicatedDms: true,
        standardAds: true,
        landingPages: true,
        leadForms: true,
        campaignCustomization: true,
        creativeEdits: true,
        localCreative: true,
        strategyCalls: true,
        updateEmails: true,
        discounts: true
      }
    }
  ];

  nonTiers: Array<{
    name: string;
    id: number;
    description: string;
    singlePrice: number | null;
    multiPrice: number | null;
    singleSignupFee: number | null;
    multiSignupFee: number | null;
    features: Record<FeatureKey, boolean>;
  }> = [
    {
      name: 'TIER_PREMIUM',
      id: 3,
      description: 'TIER_PREMIUM_DESC',
      singlePrice: null,
      multiPrice: null,
      singleSignupFee: null,
      multiSignupFee: null,
      features: {
        reachPlatform: true,
        weeklyReports: true,
        realTimeDashboard: true,
        dedicatedDms: true,
        standardAds: true,
        landingPages: true,
        leadForms: true,
        campaignCustomization: true,
        creativeEdits: true,
        localCreative: true,
        strategyCalls: true,
        updateEmails: true,
        discounts: true
      }
    }
  ];

  constructor(
    private router: Router,
    public uService: UtilityService,
    private appService: AppService,
    private subscriptionApiService: SubscriptionApiService,
    private shopApiService: ShopApiService,
    private clientService: ClientsApiService,
    private translate: TranslateService
  ) {
    this.userRoles = [...this.uService.getUserRoles()];
    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
        this.isExternal = u.type === ROLES_TYPE.External;
      });
    if (this.activeUser.roles[0].code === 8) {
      // Employee - Shouldn't see the shop
      this.router.navigate(['/pages/dashboard']);
    }
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('SHOP');

    if (this.isExternal) {
      this.getCompanyProducts();
    } else {
      this.router.navigate(['/pages/shop/products']);
    }
      
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  selectTier(tier: any) {
    // console.log('Selected Tier:', tier);
    this.router.navigate(['/pages/shop/products'], { queryParams: { t: tier.id } }); // Navigate to the products list with tier name
  }

  getCompanyProducts() {
    if (this.userRoles.includes(9)) {
      this.loadSubscriptionEmployeeProducts();
      return;
    }

    this.shopApiService.getAllProducts()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.processCompanyProducts(response.data || []);
      });
  }

  private loadSubscriptionEmployeeProducts(): void {
    this.appService.getAllCampaigns$()
      .pipe(skip(1), takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const clientIds: string[] = Array.from(new Set<string>((response?.data || []).map((campaign: any) => campaign.clientId).filter(Boolean)));

        if (!clientIds.length) {
          this.companyProducts = [];
          this.tieredProducts = [];
          this.isTiered = false;
          return;
        }

        forkJoin(clientIds.map((clientId: string) => this.clientService.getClientDetails(clientId).pipe(takeUntil(this.destroyed$))))
          .pipe(takeUntil(this.destroyed$))
          .subscribe((clientResponses: any[]) => {
            const clientBrands: string[] = Array.from(new Set<string>(clientResponses.map((clientResponse: any) => clientResponse?.data?.brand).filter(Boolean)));

            this.shopApiService.getProducts()
              .pipe(takeUntil(this.destroyed$))
              .subscribe((productsResponse: any) => {
                const visibleProducts = (productsResponse.data || []).filter((product: IProductMain) =>
                  !product.isBundleOnly && clientBrands.some((brand: string) => product.companyNames?.indexOf(brand) > -1)
                );

                this.processCompanyProducts(Array.from(new Map<string, IProductMain>(visibleProducts.map((product: IProductMain) => [product.id, product])).values()));
              });
          });
      });

    this.appService.fetchAllCampaigns({});
  }

  private processCompanyProducts(products: IProductMain[]): void {
    this.companyProducts = [];
    this.tieredProducts = [];
    this.isTiered = false;
    this.hasSetup = false;

    const productPromises = products.map((p: IProductMain) =>
      this.shopApiService.getProductDetails(p.id)
        .pipe(takeUntil(this.destroyed$))
        .toPromise()
        .then((res: any) => {
          const productDetails = res.data;

          if (!this.companyProducts.some(product => product.id === productDetails.id)) {
            this.companyProducts.push(productDetails);
          }

          this.pricingTiers.forEach(tier => {
            if (this.isTierProduct(productDetails.name ?? productDetails.display ?? productDetails.bundleName, tier.name) && productDetails.productsInBundle) {
              this.isTiered = true;
              this.companyProducts = this.companyProducts.filter(product => product.id !== productDetails.id);

              if (!this.tieredProducts.includes(tier.name)) {
                this.tieredProducts.push(tier.name);
              }

              productDetails.productsInBundle.forEach((bundleProduct: any) => {
                if (this.isTierProduct(bundleProduct.name ?? bundleProduct.display ?? bundleProduct.bundleName, tier.name)) {
                  if (bundleProduct.data.signupFee && bundleProduct.data.signupFee > 0) {
                    this.hasSetup = true;
                  }
                  if (this.isSinglePlatformProduct(bundleProduct.name ?? bundleProduct.display ?? bundleProduct.bundleName)) {
                    tier.singlePrice = bundleProduct.data.price;
                    tier.singleSignupFee = bundleProduct.data.signupFee;
                  }
                  if (this.isMultiPlatformProduct(bundleProduct.name ?? bundleProduct.display ?? bundleProduct.bundleName)) {
                    tier.multiPrice = bundleProduct.data.price;
                    tier.multiSignupFee = bundleProduct.data.signupFee;
                  }
                }
              });
            }
          });
        })
    );

    Promise.all(productPromises).then(() => {
      if (!this.isTiered) {
        this.router.navigate(['/pages/shop/products']);
      } else {
        this.appService.getAllUserSubscription()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: any) => {
            if (response.length) {
              const subscriptions = response.filter((item: any) => item.status !== 'Canceled');
              let foundTier = false;
              for (const subscription of subscriptions) {
                if (foundTier) break;
                this.subscriptionApiService.getSubscription(subscription.id)
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe((subscriptionResponse: any) => {
                    if (subscriptionResponse.good && !foundTier) {
                      const subscriptionProducts = subscriptionResponse.subscription.products;
                      for (const product of subscriptionProducts) {
                        const productLabel = product.name ?? product.display ?? product.bundleName;
                        if (this.isTierProduct(productLabel, 'TIER_BASIC')) {
                          this.existingSubscriptionTier = 1;
                          foundTier = true;
                          break;
                        } else if (this.isTierProduct(productLabel, 'TIER_STANDARD')) {
                          this.existingSubscriptionTier = 2;
                          foundTier = true;
                          break;
                        } else if (this.isTierProduct(productLabel, 'TIER_PREMIUM')) {
                          this.existingSubscriptionTier = 3;
                          foundTier = true;
                          break;
                        }
                      }
                    }
                  });
              }
            }
          });
        this.appService.fetchAllUserSubscriptions();
      }
    });
  }
}
